/**
 * Hujjat preview'i geometriyasi -- sof funksiyalar (test: preview.test.ts). Rasm sahnaga sig'diriladi (zoom 1), kattalashtiriladi
 * va buriladi; OCR bergan matn qutilari ([ymin, xmin, ymax, xmax], 0-1000) rasm ustiga foizda qo'yiladi. UI: pages/drivers/DocumentPreview.tsx.
 */
export type Rotation = 0 | 90 | 180 | 270;
export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 6;
const ZOOM_STEP = 1.25;

export const rotateNext = (r: Rotation): Rotation => (((r + 90) % 360) as Rotation);

/** Kattalashtirish qadami (25%), chegaralar ichida; 1 -- sig'dirilgan */
export function stepZoom(zoom: number, dir: 1 | -1): number {
  const next = dir > 0 ? zoom * ZOOM_STEP : zoom / ZOOM_STEP;
  return Math.round(Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, next)) * 100) / 100;
}

export interface Size {
  w: number;
  h: number;
}

export interface Layout {
  /** Sahnada joy oladigan quti (burilganda eni-bo'yi almashadi) */
  box: Size;
  /** Rasmning o'zi (burilmagan holatda) */
  img: Size;
  /** Rasm qutining markazida tursin */
  offset: { x: number; y: number };
}

/** Rasm sahnaga sig'adigan o'lcham (kichik rasm ham sahnagacha kattalashadi), so'ng zoom; burilganda sig'dirish almashgan tomonlar bo'yicha */
export function fitLayout(natural: Size, stage: Size, rot: Rotation, zoom: number): Layout {
  if (natural.w <= 0 || natural.h <= 0 || stage.w <= 0 || stage.h <= 0) return { box: { w: 0, h: 0 }, img: { w: 0, h: 0 }, offset: { x: 0, y: 0 } };
  const rotated = rot === 90 || rot === 270;
  const availW = rotated ? stage.h : stage.w;
  const availH = rotated ? stage.w : stage.h;
  const s = Math.min(availW / natural.w, availH / natural.h) * zoom;
  const img = { w: Math.round(natural.w * s), h: Math.round(natural.h * s) };
  const box = rotated ? { w: img.h, h: img.w } : { ...img };
  return { box, img, offset: { x: (box.w - img.w) / 2, y: (box.h - img.h) / 2 } };
}

/** OCR qutisi -> rasmga nisbatan foizli joylashuv (rasm bilan birga buriladi va kattalashadi); `pad` -- bosish maydoni
 * matndan sal kengroq (0-1000 birlikda), chunki OCR qutilari harf balandligida tor keladi */
export function boxStyle(box: [number, number, number, number], pad = 5): { left: string; top: string; width: string; height: string } {
  const [y0, x0, y1, x1] = box;
  const l = Math.max(0, x0 - pad);
  const t = Math.max(0, y0 - pad);
  const r = Math.min(1000, x1 + pad);
  const b = Math.min(1000, y1 + pad);
  return { left: `${l / 10}%`, top: `${t / 10}%`, width: `${(r - l) / 10}%`, height: `${(b - t) / 10}%` };
}

/** Oldingi/keyingi hujjat (ro'yxat tartibida), hozirgisining o'rni */
export function neighbors<T extends { id: number }>(docs: T[], id: number): { prev: T | null; next: T | null; index: number; total: number } {
  const index = docs.findIndex((d) => d.id === id);
  if (index < 0) return { prev: null, next: null, index: -1, total: docs.length };
  return { prev: docs[index - 1] ?? null, next: docs[index + 1] ?? null, index, total: docs.length };
}

/** Rasm ustida matn qatorlari bormi (faqat rasm, OCR tugagan va ruxsat bor) */
export function hasLiveText(doc: { content_type: string; ocr: { lines?: unknown[] } | null }): boolean {
  return doc.content_type.startsWith("image/") && !!doc.ocr?.lines?.length;
}
